import axios, { AxiosResponse } from "axios";
import Cookies from "js-cookie";
import {
  applySessionApiTokenToAxiosConfig,
  setSessionApiToken,
  clearSessionApiToken,
} from "./serviceApiKey";
// import { PublicClientApplication } from "@azure/msal-browser";
declare const BASEPATH: string;
declare const USERSERVICE: string;
declare const APP_NAME: string;
const WEBSOCKET_PORT = 6001,
  WEBSOCKET_KEY = "websocket_key";
import RoleApi from "@/API/Role/Role";
const roleApi = new RoleApi();

export default class Auth {
  static USER: Record<string, any> = {};
  static AUTHORIZED: boolean = false;
  static SESSON: Record<string, any> = {};
  static ROLE: Record<string, any> = {};
  static logoutTimer: ReturnType<typeof setTimeout> | null = null;
  static isCheckingAuth: boolean = false; // Prevent multiple simultaneous auth checks

  static LANGUAGE = [
    { name: "English", code: "en" },
    { name: "Arabic", code: "ar" },
  ];

  static SETTINGS = {
    user: {},
    system: {
      comment_approve: true,
    },
  };

  static TABLE_PERMISSIONS = {};

  /** Query params for `user-info` (full profile + relations). */
  private static readonly userInfoParams = {
    "with[]": ["department", "role", "job", "manager", "teams"],
  };

  // static baseURL: string = BASEPATH;
  static baseURL: string = USERSERVICE;

  static run() {
    axios.defaults.baseURL = this.baseURL;
    // Set up axios interceptors for better error handling
    this.setupAxiosInterceptors();
  }

  /**
   * Setup axios interceptors for automatic token refresh and error handling
   */
  static setupAxiosInterceptors() {
    // Request interceptor to add token to requests
    axios.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        applySessionApiTokenToAxiosConfig(config);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for handling 401 errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && this.AUTHORIZED) {
          this.logOut();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get stored token from cookies
   */
  static getStoredToken(): string {
    let fullToken = "";
    let i = 1;
    while (Cookies.get(`${APP_NAME}_api_token_part${i}`)) {
      fullToken += Cookies.get(`${APP_NAME}_api_token_part${i}`);
      i++;
    }
    return fullToken;
  }

  /**
   * Store token in cookies with chunking
   */
  static storeToken(token: string, remember_me: boolean = false): void {
    // Clear existing token parts first
    this.clearStoredToken();

    const partSize = 3000;
    const totalParts = Math.ceil(token.length / partSize);
    const expires = remember_me ? 30 : 1; // 30 days if remember me, 1 day otherwise

    for (let i = 0; i < totalParts; i++) {
      const part = token.substring(i * partSize, (i + 1) * partSize);
      Cookies.set(`${APP_NAME}_api_token_part${i + 1}`, part, { expires });
      // Cookies.set(`${APP_NAME}_api_token_part${i + 1}`, part, {
      //   expires,
      //   secure: true,
      //   sameSite: 'Strict',
      // });
    }
  }

  /**
   * Clear stored token from cookies
   */
  static clearStoredToken(): void {
    let i = 1;
    while (Cookies.get(`${APP_NAME}_api_token_part${i}`)) {
      Cookies.remove(`${APP_NAME}_api_token_part${i}`);
      // Cookies.remove(`${APP_NAME}_api_token_part${i}`, {
      //   secure: true,
      //   sameSite: 'Strict',
      // });
      i++;
    }
  }

  /**
   * Login user with credentials
   */
  static async logIn(
    login: string,
    password: string,
    remember_me = false
  ): Promise<boolean> {
    try {
      axios.defaults.baseURL = USERSERVICE;
      const response: AxiosResponse = await axios.post("login", {
        login,
        password,
        remember_me,
      });

      const token    = response.data.data.token;
      const apiToken = response.data.data.api_token as string | undefined;

      // Store JWT in cookies (chunked) and per-session HMAC token in memory only.
      this.storeToken(token, remember_me);
      if (apiToken) setSessionApiToken(apiToken);

      // Set axios default header
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      const userInfoResponse = await axios.get("user-info", {
        params: this.userInfoParams,
      });
      await this.setData(userInfoResponse);

      // Setup auto logout timer (1 hour)
      this.setupLogoutTimer();

      return true;
    } catch (error: any) {
      if (error.response) {
        const { status, data } = error.response;
        throw new Error(data.errors?.[0]?.[0] || data.message || "Login failed");
      } else {
        throw new Error("An unexpected error occurred during login.");
      }
    }
  }

  /**
   * Check if user is logged in
   */
  static async loggedIn(): Promise<boolean> {
    // If already authorized and user data exists, return immediately
    if (this.AUTHORIZED && this.USER && Object.keys(this.USER).length > 0) {
      return true;
    }

    // Prevent multiple simultaneous auth checks
    if (this.isCheckingAuth) {
      // Wait for ongoing check to complete
      while (this.isCheckingAuth) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      return this.AUTHORIZED;
    }

    this.isCheckingAuth = true;

    try {
      axios.defaults.baseURL = this.baseURL;

      const fullToken = this.getStoredToken();

      if (!fullToken) {
        this.isCheckingAuth = false;
        this.AUTHORIZED = false;
        return false;
      }

      // Check if token is already set in axios headers
      if (!axios.defaults.headers.common.Authorization) {
        axios.defaults.headers.common.Authorization = `Bearer ${fullToken}`;
      }

      // Only fetch user info if we don't have it
      if (!this.USER || Object.keys(this.USER).length === 0) {
        const response: AxiosResponse = await axios.get("user-info", {
          params: this.userInfoParams,
        });
        await this.setData(response);
        // if (response.data.data.department && response.data.data.job) {
        //   response.data.data.is_manager =
        //     response.data.data?.department?.manager_id ==
        //     response.data.data?.job?.position_id;
        // }
      }
      this.setupLogoutTimer();
      this.isCheckingAuth = false;
      return true;

    } catch (error) {
      this.AUTHORIZED = false;
      this.USER = {};
      this.clearStoredToken();
      this.isCheckingAuth = false;
      return false;
    }
  }
  /**
   * Set user data from response
   */
  static async setData(response: AxiosResponse): Promise<void> {
    try {
      this.AUTHORIZED = true;
      this.USER = response.data.data;

      // Get role data if user has job_id and department_id
      if (this.USER.job_id && this.USER.department_id) {
        try {
          this.ROLE = await roleApi.getRoleJobWithDepartment(
            this.USER.job_id,
            this.USER.department_id
          );
        } catch (roleError) {
          this.ROLE = {};
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Setup automatic logout timer
   */
  static setupLogoutTimer(): void {
    // Clear existing timer
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }

    // Set new timer for 8 hours
    const tokenDuration = 60 * 60 * 8 * 1000; // 8 hours in milliseconds
    this.logoutTimer = setTimeout(() => {
      this.logOut();
    }, tokenDuration);
  }

  /**
   * Logout user
   */
  static logOut(): boolean {
    try {
      // Clear stored tokens
      this.clearStoredToken();
      clearSessionApiToken();

      // Clear logout timer
      if (this.logoutTimer) {
        clearTimeout(this.logoutTimer);
        this.logoutTimer = null;
      }

      // Reset auth state
      this.AUTHORIZED = false;
      this.USER = {};
      this.ROLE = {};
      this.isCheckingAuth = false;

      // Clear axios auth header
      delete axios.defaults.headers.common.Authorization;

      // Clear local storage
      localStorage.removeItem('navActiveState');
      localStorage.removeItem('selectedLanguageCode');

      // Redirect to login
      window.location.href = "/login";

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current auth token
   */
  static async getAuthToken(): Promise<string | null> {
    try {
      return this.getStoredToken() || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user is super admin
   */
  static isSuperAdmin(): boolean {
    return this.USER?.super === true || this.USER?.super == 'true';
  }

  /**
   * Get user info
   */
  static getUser(): Record<string, any> {
    return this.USER;
  }

  /**
   * Get user role
   */
  static getRole(): Record<string, any> {
    return this.ROLE;
  }

  /**
   * Refresh user data
   */
  static async refreshUserData(): Promise<boolean> {
    try {
      const response: AxiosResponse = await axios.get("user-info", {
        params: this.userInfoParams,
      });
      // if (response.data.data.department && response.data.data.job) {
      //   response.data.data.is_manager =
      //     response.data.data?.department?.manager_id ==
      //     response.data.data?.job?.position_id;
      // }
      await this.setData(response);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Setup WebSocket connection (placeholder for future implementation)
   */
  static setupWebSocketConnection(): void {
    // WebSocket implementation would go here
    // Currently commented out in the original code
  }

  /**
   * Login with Microsoft Graph (Azure AD)
   * Uses MSAL popup to get auth code, then sends it to the backend
   * Backend pulls user data, department, job from MS Graph and creates user if not exists
   */
  // static async msGraphLogin(remember_me = false): Promise<boolean> {
  //   try {
  //     axios.defaults.baseURL = USERSERVICE;

  //     // 1. Fetch MS Graph config from backend
  //     const configResponse = await axios.get("auth/microsoft/config");
  //     const { client_id, tenant_id, redirect_uri, scopes } = configResponse.data;

  //     // 2. Initialize MSAL
  //     const msalInstance = new PublicClientApplication({
  //       auth: {
  //         clientId: client_id,
  //         authority: `https://login.microsoftonline.com/${tenant_id}`,
  //         redirectUri: redirect_uri,
  //       },
  //       cache: {
  //         cacheLocation: "sessionStorage",
  //       },
  //     });

  //     await msalInstance.initialize();

  //     // 3. Open popup to get authorization code
  //     const loginResponse = await msalInstance.loginPopup({
  //       scopes: scopes.split(" "),
  //     });

  //     if (!loginResponse || !loginResponse.accessToken) {
  //       throw new Error("Microsoft login was cancelled or failed.");
  //     }

  //     // 4. Get authorization code via acquireTokenSilent for the backend
  //     // Since MSAL popup gives us tokens directly, we use the code flow via backend
  //     // Re-acquire with code response type by sending the id token to backend
  //     const callbackResponse: AxiosResponse = await axios.post(
  //       "auth/microsoft/callback",
  //       { code: loginResponse.code || loginResponse.accessToken }
  //     );

  //     const token = callbackResponse.data.data.token;

  //     // 5. Store token and set auth
  //     this.storeToken(token, remember_me);
  //     axios.defaults.headers.common.Authorization = `Bearer ${token}`;

  //     await this.setData(callbackResponse);
  //     this.setupLogoutTimer();

  //     return true;
  //   } catch (error: any) {
  //     console.error("Microsoft Graph login error:", error);
  //     if (error.response) {
  //       throw new Error(
  //         error.response.data?.errors?.[0]?.[0] ||
  //         error.response.data?.message ||
  //         "Microsoft login failed"
  //       );
  //     }
  //     throw new Error(error.message || "Microsoft login failed");
  //   }
  // }
}